import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { jod } from "@astroplanet/shared";
import { hrApi, useDepartments, useDeviceUsers, useEmployee, useEmployees, useLeaveTypes, useSchedule, useShifts } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import { localName, money } from "../../lib/format";
import type { Contract, HrLevel } from "../../lib/typesHr";
import { Empty } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { dateOnly, Field, ReasonDialog, StatusPill, Table, todayIso, useAction, useEmpName } from "./shared";

type Tab = "profile" | "contract" | "schedule" | "leave" | "device";
const IBAN = /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/;

interface FormState {
  employeeNo: string; fullName: string; fullNameAr: string; nationalId: string; gender: string; birthDate: string; phone: string; email: string; address: string; emergencyContact: string;
  hireDate: string; departmentId: string; managerId: string; jobTitle: string; jobTitleAr: string; paymentMethod: string; bankName: string; bankIban: string; socialSecurityNo: string; taxNo: string; staffId: string;
}
const EMPTY: FormState = { employeeNo: "", fullName: "", fullNameAr: "", nationalId: "", gender: "", birthDate: "", phone: "", email: "", address: "", emergencyContact: "", hireDate: todayIso(), departmentId: "", managerId: "", jobTitle: "", jobTitleAr: "", paymentMethod: "bank_transfer", bankName: "", bankIban: "", socialSecurityNo: "", taxNo: "", staffId: "" };

const nn = (s: string) => (s.trim() === "" ? null : s.trim());

/** Create (id = null) or edit an employee. Personal / bank data is only present when the server lets this person see it. */
function ProfileForm({ id, canEdit, onSaved }: { id: string | null; canEdit: boolean; onSaved: (id: string) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const detail = useEmployee(id);
  const depts = useDepartments();
  const mgrs = useEmployees({ status: "active" });
  const name = useEmpName();
  const { act, busy } = useAction();
  const [f, setF] = useState<FormState>(EMPTY);
  const [terminate, setTerminate] = useState(false);
  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    const d = detail.data;
    if (!id || !d) return;
    const e = d.employee, p = d.private;
    setF({
      employeeNo: e.employee_no, fullName: e.full_name, fullNameAr: e.full_name_ar ?? "", nationalId: p?.national_id ?? "", gender: e.gender ?? "", birthDate: p?.birth_date?.slice(0, 10) ?? "", phone: e.phone ?? "", email: e.email ?? "",
      address: p?.address ?? "", emergencyContact: p?.emergency_contact ?? "", hireDate: e.hire_date.slice(0, 10), departmentId: e.department_id ?? "", managerId: e.manager_id ?? "", jobTitle: e.job_title ?? "", jobTitleAr: e.job_title_ar ?? "",
      paymentMethod: e.payment_method ?? "bank_transfer", bankName: p?.bank_name ?? "", bankIban: p?.bank_iban ?? "", socialSecurityNo: p?.social_security_no ?? "", taxNo: p?.tax_no ?? "", staffId: e.staff_id ?? "",
    });
  }, [id, detail.data]);

  const ibanOk = f.bankIban.trim() === "" || IBAN.test(f.bankIban.trim().toUpperCase());
  const valid = f.fullName.trim().length >= 2 && !!f.hireDate && ibanOk;

  async function save() {
    const body = {
      ...(id ? {} : { employeeNo: nn(f.employeeNo) ?? undefined }), fullName: f.fullName.trim(), fullNameAr: nn(f.fullNameAr), nationalId: nn(f.nationalId), gender: f.gender || null, birthDate: nn(f.birthDate), phone: nn(f.phone), email: nn(f.email),
      address: nn(f.address), emergencyContact: nn(f.emergencyContact), hireDate: f.hireDate, departmentId: f.departmentId || null, managerId: f.managerId || null, jobTitle: nn(f.jobTitle), jobTitleAr: nn(f.jobTitleAr),
      paymentMethod: f.paymentMethod, bankName: nn(f.bankName), bankIban: nn(f.bankIban.toUpperCase()), socialSecurityNo: nn(f.socialSecurityNo), taxNo: nn(f.taxNo), staffId: f.staffId || null,
    };
    const r = id ? await act(() => hrApi.patch(`/employees/${id}`, body), "hr.saved").then((x) => (x ? { id } : undefined)) : await act(() => hrApi.post<{ id: string }>("/employees", body), "hr.saved");
    if (r) onSaved(r.id);
  }

  const dis = !canEdit;
  const input = (k: keyof FormState, label: string, extra: { type?: string; num?: boolean } = {}) => (
    <Field label={label}><input className={`field ${extra.num ? "num" : ""}`} type={extra.type ?? "text"} value={f[k]} onChange={set(k)} disabled={dis} /></Field>
  );
  return (
    <div className="space-y-4">
      {id && detail.data && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <StatusPill status={detail.data.employee.status} />
          <span className="font-semibold text-gray-500">{t("hr.hired")}: <span className="num">{dateOnly(detail.data.employee.hire_date, locale)}</span></span>
          {detail.data.manager && <span className="font-semibold text-gray-500">{t("hr.manager")}: {name(detail.data.manager)}</span>}
          {detail.data.employee.termination_date && <span className="font-semibold text-red-600">{t("hr.terminatedOn")}: <span className="num">{dateOnly(detail.data.employee.termination_date, locale)}</span></span>}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {!id && input("employeeNo", t("hr.employeeNo"), { num: true })}
        {input("fullName", t("hr.fullName"))}
        {input("fullNameAr", t("hr.fullNameAr"))}
        {input("jobTitle", t("hr.jobTitle"))}
        {input("jobTitleAr", t("hr.jobTitleAr"))}
        <Field label={t("hr.department")}>
          <select className="field" value={f.departmentId} onChange={set("departmentId")} disabled={dis}><option value="">—</option>{(depts.data ?? []).map((d) => <option key={d.id} value={d.id}>{localName(d, locale)}</option>)}</select>
        </Field>
        <Field label={t("hr.manager")}>
          <select className="field" value={f.managerId} onChange={set("managerId")} disabled={dis}><option value="">—</option>{(mgrs.data ?? []).filter((m) => m.id !== id).map((m) => <option key={m.id} value={m.id}>{name(m)}</option>)}</select>
        </Field>
        {input("hireDate", t("hr.hired"), { type: "date", num: true })}
        {input("phone", t("hr.phone"), { num: true })}
        {input("email", t("hr.email"))}
        <Field label={t("hr.gender")}>
          <select className="field" value={f.gender} onChange={set("gender")} disabled={dis}><option value="">—</option><option value="male">{t("hr.male")}</option><option value="female">{t("hr.female")}</option></select>
        </Field>
        {input("nationalId", t("hr.nationalId"), { num: true })}
        {input("birthDate", t("hr.birthDate"), { type: "date", num: true })}
        {input("address", t("hr.address"))}
        {input("emergencyContact", t("hr.emergencyContact"))}
        <Field label={t("hr.paymentMethod")}>
          <select className="field" value={f.paymentMethod} onChange={set("paymentMethod")} disabled={dis}>{["bank_transfer", "cash", "cliq", "cheque"].map((m) => <option key={m} value={m}>{t(`hr.pay.${m}`)}</option>)}</select>
        </Field>
        {input("bankName", t("hr.bankName"))}
        <Field label="IBAN" hint={!ibanOk ? t("hr.ibanInvalid") : undefined}><input className={`field num ${ibanOk ? "" : "!border-red-400"}`} value={f.bankIban} onChange={set("bankIban")} disabled={dis} /></Field>
        {input("socialSecurityNo", t("hr.socialSecurityNo"), { num: true })}
        {input("taxNo", t("hr.taxNo"), { num: true })}
      </div>
      {canEdit && (
        <div className="flex flex-wrap justify-between gap-2 border-t border-gray-100 pt-3">
          {id && detail.data?.employee.status !== "terminated" ? <button type="button" className="btn-ghost text-red-700" onClick={() => setTerminate(true)}>{t("hr.terminate")}</button> : <span />}
          <button type="button" className="btn-primary" disabled={!valid || busy} onClick={() => void save()}>{t("hr.save")}</button>
        </div>
      )}
      <ReasonDialog open={terminate} title={t("hr.terminateTitle")} confirmLabel={t("hr.terminate")} onClose={() => setTerminate(false)}
        onConfirm={(reason) => { setTerminate(false); void act(() => hrApi.post(`/employees/${id}/terminate`, { date: todayIso(), reason }), "hr.done"); }} />
    </div>
  );
}

function ContractTab({ id, level }: { id: string; level: HrLevel }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const detail = useEmployee(id);
  const { act, busy } = useAction();
  const [adding, setAdding] = useState(false);
  const [payType, setPayType] = useState<"monthly" | "daily" | "hourly">("monthly");
  const [amount, setAmount] = useState("");
  const [hours, setHours] = useState("8");
  const [days, setDays] = useState("5");
  const [ot, setOt] = useState("15000");
  const [from, setFrom] = useState(todayIso());
  const fils = Math.round(Number(amount) * 1000);
  const rateOf = (c: Contract) => (c.pay_type === "monthly" ? c.monthly_salary_fils : c.pay_type === "daily" ? c.daily_rate_fils : c.hourly_rate_fils) ?? 0;

  async function create() {
    const key = payType === "monthly" ? "monthlySalaryFils" : payType === "daily" ? "dailyRateFils" : "hourlyRateFils";
    const r = await act(() => hrApi.post(`/employees/${id}/contracts`, { payType, [key]: jod(Number(amount)), standardHoursPerDay: Number(hours), workingDaysPerWeek: Number(days), overtimeMultiplierBp: Number(ot), effectiveFrom: from }), "hr.contract.created");
    if (r) { setAdding(false); setAmount(""); }
  }
  const contracts = detail.data?.contracts ?? [];
  if (!detail.data?.private && level < 2 && contracts.length === 0) return <Empty>{t("hr.contract.hidden")}</Empty>;
  return (
    <div className="space-y-3">
      {contracts.length === 0 ? <Empty>{t("hr.contract.none")}</Empty> : (
        <Table head={[t("hr.contract.basis"), t("hr.contract.rate"), t("hr.period"), t("hr.status.label"), ""]}>
          {contracts.map((c) => (
            <tr key={c.id}>
              <td>{t(`hr.contract.${c.pay_type}`)}</td>
              <td className="num font-bold">{money(rateOf(c))} {t("currency")}</td>
              <td className="num">{dateOnly(c.effective_from, locale)} → {c.effective_to ? dateOnly(c.effective_to, locale) : "…"}</td>
              <td><StatusPill status={c.status} /></td>
              <td className="text-end">{c.status === "draft" && level >= 2 && <button type="button" className="btn-success" disabled={busy} onClick={() => void act(() => hrApi.post(`/contracts/${c.id}/activate`), "hr.contract.activated")}>{t("hr.contract.activate")}</button>}</td>
            </tr>
          ))}
        </Table>
      )}
      {level >= 2 && !adding && <button type="button" className="btn-primary" onClick={() => setAdding(true)}>{t("hr.contract.new")}</button>}
      {adding && (
        <div className="grid gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:grid-cols-2">
          <Field label={t("hr.contract.basis")}><select className="field" value={payType} onChange={(e) => setPayType(e.target.value as typeof payType)}>{(["monthly", "daily", "hourly"] as const).map((p) => <option key={p} value={p}>{t(`hr.contract.${p}`)}</option>)}</select></Field>
          <Field label={`${t("hr.contract.rate")} (${t("currency")})`}><input className="field num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="600.000" /></Field>
          <Field label={t("hr.contract.hoursPerDay")}><input className="field num" value={hours} onChange={(e) => setHours(e.target.value)} /></Field>
          <Field label={t("hr.contract.daysPerWeek")}><input className="field num" value={days} onChange={(e) => setDays(e.target.value)} /></Field>
          <Field label={t("hr.contract.otMultiplier")}><select className="field" value={ot} onChange={(e) => setOt(e.target.value)}>{["10000", "12500", "15000", "20000"].map((v) => <option key={v} value={v}>×{Number(v) / 10000}</option>)}</select></Field>
          <Field label={t("hr.contract.effectiveFrom")}><input type="date" className="field num" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <div className="flex justify-end gap-2 sm:col-span-2"><button type="button" className="btn-ghost" onClick={() => setAdding(false)}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={busy || !(fils > 0)} onClick={() => void create()}>{t("hr.save")}</button></div>
          <p className="text-[10px] font-semibold text-gray-500 sm:col-span-2">{t("hr.contract.draftHelp")}</p>
        </div>
      )}
    </div>
  );
}

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]; // ISO: Monday … Sunday

function ScheduleTab({ id }: { id: string }) {
  const { t } = useTranslation();
  const sched = useSchedule(id);
  const shifts = useShifts();
  const { act, busy } = useAction();
  const [from, setFrom] = useState(todayIso());
  const [pattern, setPattern] = useState<Record<number, string>>({});
  useEffect(() => {
    const today = todayIso();
    const m: Record<number, string> = {};
    for (const p of sched.data?.patterns ?? []) if (p.shift_template_id) m[p.weekday] = p.shift_template_id;
    void today;
    setPattern(m);
  }, [sched.data]);
  const active = (shifts.data ?? []).filter((s) => s.active);
  if (active.length === 0) return <Empty>{t("hr.schedule.noShifts")}</Empty>;
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">{t("hr.schedule.help")}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {WEEKDAYS.map((d) => (
          <label key={d} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-bold">
            {t(`hr.weekday.${d}`)}
            <select className="field !w-44" value={pattern[d] ?? ""} onChange={(e) => setPattern((p) => ({ ...p, [d]: e.target.value }))} aria-label={t(`hr.weekday.${d}`)}>
              <option value="">{t("hr.schedule.off")}</option>
              {active.map((s) => <option key={s.id} value={s.id}>{s.name} {s.start_time}–{s.end_time}</option>)}
            </select>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Field label={t("hr.schedule.validFrom")}><input type="date" className="field num" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <button type="button" className="btn-primary" disabled={busy} onClick={() => void act(() => hrApi.put(`/employees/${id}/schedule`, { validFrom: from, pattern: WEEKDAYS.map((d) => ({ weekday: d, shiftId: pattern[d] || null })) }), "hr.saved")}>{t("hr.save")}</button>
      </div>
    </div>
  );
}

function LeaveTab({ id, level }: { id: string; level: HrLevel }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const detail = useEmployee(id);
  const types = useLeaveTypes();
  const { act, busy } = useAction();
  const [typeId, setTypeId] = useState("");
  const [d, setD] = useState("");
  const [note, setNote] = useState("");
  return (
    <div className="space-y-3">
      {(detail.data?.balances ?? []).length === 0 ? <Empty>{t("hr.leave.noBalances")}</Empty> : (
        <div className="grid gap-3 sm:grid-cols-3">
          {detail.data!.balances.map((b) => (
            <div key={`${b.leave_type_id}${b.balance_year}`} className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
              <div className="text-[10px] font-black uppercase text-gray-500">{localName({ name: b.name, nameAr: b.name_ar }, locale)} · <span className="num">{b.balance_year}</span></div>
              <div className="num mt-1 font-mono text-xl font-black text-cosmic-purple">{b.balance}</div>
              <div className="text-[10px] font-semibold text-gray-500">{t("hr.leave.accrued")} <span className="num">{b.accrued}</span> · {t("hr.leave.taken")} <span className="num">{b.taken}</span></div>
            </div>
          ))}
        </div>
      )}
      {level >= 2 && (
        <div className="grid gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:grid-cols-4">
          <Field label={t("hr.leave.type")}><select className="field" value={typeId} onChange={(e) => setTypeId(e.target.value)}><option value="">—</option>{(types.data ?? []).filter((x) => x.tracks_balance).map((x) => <option key={x.id} value={x.id}>{localName(x, locale)}</option>)}</select></Field>
          <Field label={t("hr.leave.adjustDays")}><input className="field num" inputMode="decimal" value={d} onChange={(e) => setD(e.target.value)} placeholder="+2 / -1" /></Field>
          <Field label={t("hr.reason")}><input className="field" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          <div className="flex items-end"><button type="button" className="btn-primary w-full" disabled={busy || !typeId || !Number(d) || note.trim().length < 3} onClick={() => void act(() => hrApi.post("/leave/ledger-adjust", { employeeId: id, typeId, days: Number(d), note: note.trim() }), "hr.saved").then(() => { setD(""); setNote(""); })}>{t("hr.leave.adjust")}</button></div>
        </div>
      )}
    </div>
  );
}

function DeviceTab({ id }: { id: string }) {
  const { t } = useTranslation();
  const users = useDeviceUsers();
  const { act, busy } = useAction();
  const [pin, setPin] = useState("");
  const mine = (users.data ?? []).filter((u) => u.employee_id === id && u.active);
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">{t("hr.devices.pinHelp")}</p>
      {mine.length === 0 ? <Empty>{t("hr.devices.noPins")}</Empty> : (
        <ul className="space-y-1">
          {mine.map((u) => (
            <li key={u.id} className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2 text-xs font-bold">
              <span><span className="num font-mono">PIN {u.pin}</span> · {u.device_name ?? t("hr.devices.allDevices")}</span>
              <button type="button" className="btn-ghost text-red-700" disabled={busy} onClick={() => void act(() => hrApi.del(`/device-users/${u.id}`), "hr.done")}>{t("hr.remove")}</button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <Field label={t("hr.devices.pin")}><input className="field num" value={pin} onChange={(e) => setPin(e.target.value)} /></Field>
        <button type="button" className="btn-primary" disabled={busy || !pin.trim()} onClick={() => void act(() => hrApi.post("/device-users", { employeeId: id, pin: pin.trim() }), "hr.saved").then(() => setPin(""))}>{t("hr.devices.enrol")}</button>
      </div>
    </div>
  );
}

export function EmployeeDialog({ id, open, onClose, level }: { id: string | null; open: boolean; onClose: () => void; level: HrLevel }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<string | null>(id);
  const [tab, setTab] = useState<Tab>("profile");
  const detail = useEmployee(current);
  const name = useEmpName();
  useEffect(() => { setCurrent(id); setTab("profile"); }, [id, open]);
  const tabs: Tab[] = current ? (level >= 2 ? ["profile", "contract", "schedule", "leave", "device"] : ["profile", "schedule", "leave"]) : ["profile"];
  return (
    <Modal open={open} size="lg" title={current ? `${name(detail.data?.employee)} · ${detail.data?.employee.employee_no ?? ""}` : t("hr.people.add")} onClose={onClose}>
      <div className="flex flex-wrap gap-1 border-b border-gray-100 pb-2" role="tablist">
        {tabs.map((x) => <button key={x} role="tab" aria-selected={tab === x} type="button" onClick={() => setTab(x)} className={`rounded-lg px-3 py-1.5 text-xs font-black ${tab === x ? "bg-cosmic-purple text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{t(`hr.people.tab.${x}`)}</button>)}
      </div>
      {tab === "profile" && <ProfileForm id={current} canEdit={level >= 2} onSaved={(nid) => { if (!current) setCurrent(nid); }} />}
      {tab === "contract" && current && <ContractTab id={current} level={level} />}
      {tab === "schedule" && current && <ScheduleTab id={current} />}
      {tab === "leave" && current && <LeaveTab id={current} level={level} />}
      {tab === "device" && current && <DeviceTab id={current} />}
    </Modal>
  );
}
